import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import CobranzaEmailStatusBadge from "./CobranzaEmailStatusBadge";
import {
  getCobranzaEmailRecipientLabel,
  getCobranzaEmailRecipientRoleLabel,
  getCobranzaEmailRecipients,
  isCobranzaTrackedEmailRecipient,
  sortCobranzaEmailRecipients,
} from "./cobranzaGestionUtils";

export default function CobranzaEmailRecipientsStatus({ entry, className = "" }) {
  const { t } = useTranslation("cobranza");
  const recipients = useMemo(
    () => sortCobranzaEmailRecipients(getCobranzaEmailRecipients(entry)),
    [entry]
  );

  if (!recipients.length) {
    return <CobranzaEmailStatusBadge entry={entry} className={className} />;
  }

  return (
    <div className={`cf-gestion-email-recipients ${className}`.trim()}>
      <div className="cf-gestion-email-recipients__header">
        <span>{t("gestionHistory.recipientTrackingTitle")}</span>
      </div>
      <div className="cf-gestion-email-recipients__table" role="table">
        <div className="cf-gestion-email-recipients__head" role="row">
          <span className="cf-gestion-email-recipients__col cf-gestion-email-recipients__col--role" role="columnheader">
            {t("gestionHistory.recipientTrackingRole")}
          </span>
          <span className="cf-gestion-email-recipients__col cf-gestion-email-recipients__col--name" role="columnheader">
            {t("gestionHistory.recipientTrackingRecipient")}
          </span>
          <span className="cf-gestion-email-recipients__col cf-gestion-email-recipients__col--status" role="columnheader">
            {t("gestionHistory.recipientTrackingStatus")}
          </span>
        </div>
        {recipients.map((recipient) => {
          const label = getCobranzaEmailRecipientLabel(recipient, t);
          const roleLabel = getCobranzaEmailRecipientRoleLabel(recipient, t);
          const email = String(recipient?.email || "").trim();
          const tracked = isCobranzaTrackedEmailRecipient(recipient);
          const statusAt = recipient?.emailStatusAt;
          const title =
            tracked && statusAt && moment(statusAt).isValid()
              ? `${label} · ${moment(statusAt).format("DD/MM/YYYY HH:mm")}`
              : label;

          return (
            <div
              key={`${recipient.role || "cc"}-${recipient.email}`}
              className="cf-gestion-email-recipients__row"
              role="row"
              title={title}
            >
              <span className="cf-gestion-email-recipients__col cf-gestion-email-recipients__col--role" role="cell">
                {roleLabel}
              </span>
              <span className="cf-gestion-email-recipients__col cf-gestion-email-recipients__col--name" role="cell">
                <span className="cf-gestion-email-recipients__name">{label}</span>
                {email && label !== email ? (
                  <span className="cf-gestion-email-recipients__email">{email}</span>
                ) : null}
              </span>
              <span className="cf-gestion-email-recipients__col cf-gestion-email-recipients__col--status" role="cell">
                {tracked ? (
                  <CobranzaEmailStatusBadge
                    entry={{
                      emailStatus: recipient?.emailStatus,
                      emailStatusAt: recipient?.emailStatusAt,
                    }}
                  />
                ) : (
                  <span className="cf-gestion-email-recipients__no-tracking">
                    {t("gestionHistory.emailStatus.noTracking")}
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
